import { createBrowserRouter, createMemoryRouter, Navigate, type RouteObject } from 'react-router-dom';
import { Suspense, lazy, type ComponentType, type LazyExoticComponent, type ReactNode } from 'react';
import { AppShell } from '@shared/layout/AppShell';
import { RouteErrorElement } from './RouteErrorElement';
import { ProtectedRoute } from './ProtectedRoute';
import { PlaceholderPage } from '@shared/ui/PlaceholderPage';
import { RouteFallback } from './RouteFallback';
import NotFound from './NotFound';
import LoginPage from '@features/auth/LoginPage';
import { NAV_BY_ID, ROUTE_IDS, type RouteId } from '@features/_navigation';

/**
 * Lazy-loaded feature elements. Each becomes its own code-split chunk.
 * Routes not registered here fall back to <PlaceholderPage/> until they
 * are implemented in Phase 4 / 5.
 *
 * Add a feature:
 *   const FooPage = lazy(() => import('@features/foo'));
 *   LAZY_ELEMENTS.foo = <Suspense fallback={<RouteFallback />}><FooPage /></Suspense>;
 */
const DashboardPage = lazy(() => import('@features/dashboard'));
const ApisPage = lazy(() => import('@features/apis'));
const TerminalsPage = lazy(() => import('@features/terminals'));
const WebhooksPage = lazy(() => import('@features/webhooks'));
const SettlementWebhooksPage = lazy(() => import('@features/settlementwebhooks'));
const VPaymentsWebhooksPage = lazy(() => import('@features/vpayments/VpaymentsWebhooksPage'));
const CloudOverviewPage = lazy(() => import('@features/cloudconnect'));
const CloudGettingStartedPage = lazy(() => import('@features/cloudconnect/GettingStartedPage'));
const CloudVerofyMovePage = lazy(() => import('@features/cloudconnect/VerofyMovePage'));
const CloudHintsTipsPage = lazy(() => import('@features/cloudconnect/HintsTipsPage'));
const SdkOverviewPage = lazy(() => import('@features/cloudconnect/sdk/SdkOverviewPage'));
const SdkPlatformGuidesPage = lazy(() => import('@features/cloudconnect/sdk/SdkPlatformGuidesPage'));
const SdkApiReferencePage = lazy(() => import('@features/cloudconnect/sdk/SdkApiReferencePage'));
const ConnectSdkTopicPage = lazy(() => import('@features/cloudconnect/sdk/ConnectSdkTopicPage'));
const CloudCreateTransactionPage = lazy(() => import('@features/cloudconnect/transactions/CreateTransactionPage'));
const CloudTransactionResponsePage = lazy(() => import('@features/cloudconnect/transactions/TransactionResponsePage'));
const CloudCancelTransactionPage = lazy(() => import('@features/cloudconnect/transactions/CancelTransactionPage'));
const CloudRefundTransactionPage = lazy(() => import('@features/cloudconnect/transactions/RefundTransactionPage'));
const SoftposOverviewPage = lazy(() => import('@features/softposconnect'));
const SoftposGettingStartedPage = lazy(() => import('@features/softposconnect/GettingStartedPage'));
const SoftposAppSetupPage = lazy(() => import('@features/softposconnect/AppSetupPage'));
const SoftposHintsTipsPage = lazy(() => import('@features/softposconnect/HintsTipsPage'));
const SoftposCertifiedDevicesPage = lazy(() => import('@features/softposconnect/CertifiedDevicesPage'));
const SoftposConfigureSdkPage = lazy(() => import('@features/softposconnect/sdk/ConfigureSdkPage'));
const SoftposIntegrationPage = lazy(() => import('@features/softposconnect/sdk/SoftposIntegrationPage'));
const SoftposIntegrationFlowPage = lazy(() => import('@features/softposconnect/sdk/IntegrationFlowPage'));
const SoftposBestPracticesPage = lazy(() => import('@features/softposconnect/sdk/BestPracticesPage'));
const SoftposCreateTransactionPage = lazy(() => import('@features/softposconnect/transactions/CreateTransactionPage'));
const SoftposTransactionResponsePage = lazy(() => import('@features/softposconnect/transactions/TransactionResponsePage'));
const SoftposCancelTransactionPage = lazy(() => import('@features/softposconnect/transactions/CancelTransactionPage'));
const SoftposRefundTransactionPage = lazy(() => import('@features/softposconnect/transactions/RefundTransactionPage'));
const VPaymentsOverviewPage = lazy(() => import('@features/vpayments'));
const VPaymentsGettingStartedPage = lazy(() => import('@features/vpayments/GettingStartedPage'));
const VPaymentsApiIntegrationPage = lazy(() => import('@features/vpayments/ApiIntegrationPage'));
const VPaymentsVTerminalApiPage = lazy(() => import('@features/vpayments/VTerminalApiPage'));
const VPaymentsPaylinksApiPage = lazy(() => import('@features/vpayments/PaylinksApiPage'));
const VPaymentsActivationPage = lazy(() => import('@features/vpayments/VpaymentsActivationPage'));
const VPaymentsIframeOverviewPage = lazy(() => import('@features/vpayments/IframeOverviewPage'));
const VPaymentsIframeHomepagePage = lazy(() => import('@features/vpayments/IframeHomepagePage'));
const VPaymentsIframeVterminalPage = lazy(() => import('@features/vpayments/IframeVterminalPage'));
const VPaymentsIframePaylinksPage = lazy(() => import('@features/vpayments/IframePaylinksPage'));
const VPaymentsIframeRefundPage = lazy(() => import('@features/vpayments/IframeRefundPage'));
const AiOpsPage = lazy(() => import('@features/aiops'));
const TreasuryPage = lazy(() => import('@features/treasury'));
const RiskPage = lazy(() => import('@features/risk'));
const CapitalPage = lazy(() => import('@features/capital'));
const TroncPage = lazy(() => import('@features/tronc'));
const IdentityPage = lazy(() => import('@features/identity'));
const SettlementPage = lazy(() => import('@features/settlement'));
const StatusPage = lazy(() => import('@features/status'));
const SupportPage = lazy(() => import('@features/support'));
const ReportingPage = lazy(() => import('@features/reporting'));
const SdksPage = lazy(() => import('@features/sdks'));
const PhpSdkPage = lazy(() => import('@features/phpsdk'));
const TypeScriptSdkPage = lazy(() => import('@features/tssdk'));
const PythonSdkPage = lazy(() => import('@features/pythonsdk'));
const GoSdkPage = lazy(() => import('@features/gosdk'));
const DotnetSdkPage = lazy(() => import('@features/dotnetsdk'));
const RubySdkPage = lazy(() => import('@features/rubysdk'));
const SwiftSdkPage = lazy(() => import('@features/swiftsdk'));
const KotlinSdkPage = lazy(() => import('@features/kotlinsdk'));
const SettingsPage = lazy(() => import('@features/settings'));
const CustomersPage = lazy(() => import('@features/customers'));
const CompaniesPage = lazy(() => import('@features/companies'));
const EstatePage = lazy(() => import('@features/estate'));
const ProductsPage = lazy(() => import('@features/products'));
const ConnectApiKeysPage = lazy(() => import('@features/connect/apikeys'));
const ConnectEstatePage = lazy(() => import('@features/connect/estate'));
const ConnectUsersPage = lazy(() => import('@features/connect/users'));
const ConnectCompaniesPage = lazy(() => import('@features/connect/companies'));
const ConnectProductsPage = lazy(() => import('@features/connect/products'));
const ConnectCustomersPage = lazy(() => import('@features/connect/customers'));
const GettingStartedPage = lazy(() => import('@features/gettingstarted'));
const McpPage = lazy(() => import('@features/mcp'));
const McpDocsPage = lazy(() => import('@features/mcp/McpDocsPage'));
const SandboxPage = lazy(() => import('@features/sandbox'));
const SandboxWebhooksPage = lazy(() => import('@features/sandbox/SandboxWebhooksPage'));
const DbSchemaPage = lazy(() => import('@features/dbschema'));
const ResidualsPage = lazy(() => import('@features/residuals'));
const SubscriptionsPage = lazy(() => import('@features/subscriptions'));
const CollectionsPage = lazy(() => import('@features/collections'));
const PayLinksPage = lazy(() => import('@features/paylinks'));
const FinancePage = lazy(() => import('@features/finance'));
const AiDocsChatPage = lazy(() => import('@features/aidocschat'));
const AiIntegrationPage = lazy(() => import('@features/aiintegration'));
const ProductionReadinessPage = lazy(() => import('@features/productionreadiness'));
const CompliancePage = lazy(() => import('@features/compliance'));
const AiOverviewPage = lazy(() => import('@features/aioverview'));
const AiIncidentPage = lazy(() => import('@features/aiincident'));
const AiAgentsPage = lazy(() => import('@features/aiagents'));
const DocumentationPage = lazy(() => import('@features/documentation'));
const PlaygroundPage = lazy(() => import('@features/playground'));
const GovernancePage = lazy(() => import('@features/governance'));

const suspended = (Component: LazyExoticComponent<ComponentType>): ReactNode => (
  <Suspense fallback={<RouteFallback />}>
    <Component />
  </Suspense>
);

const LAZY_ELEMENTS: Partial<Record<RouteId, ReactNode>> = {
  dashboard: suspended(DashboardPage),
  apis: suspended(ApisPage),
  terminals: suspended(TerminalsPage),
  webhooks: suspended(WebhooksPage),
  settlementwebhooks: suspended(SettlementWebhooksPage),
  vpaymentswebhooks: suspended(VPaymentsWebhooksPage),
  cloudconnect: suspended(CloudOverviewPage),
  ccgettingstarted: suspended(CloudGettingStartedPage),
  ccverofymove: suspended(CloudVerofyMovePage),
  ccsdk: suspended(SdkOverviewPage),
  ccsdkkotlin: suspended(ConnectSdkTopicPage),
  ccsdkkotlininstallation: suspended(ConnectSdkTopicPage),
  ccsdkkotlinauthentication: suspended(ConnectSdkTopicPage),
  ccsdkkotlininitialization: suspended(ConnectSdkTopicPage),
  ccsdkkotlintransactions: suspended(ConnectSdkTopicPage),
  ccsdkkotlinrealtimemqtt: suspended(ConnectSdkTopicPage),
  ccsdkkotlinerrorhandling: suspended(ConnectSdkTopicPage),
  ccsdkdotnet: suspended(ConnectSdkTopicPage),
  ccsdkdotnetinstallation: suspended(ConnectSdkTopicPage),
  ccsdkdotnetauthentication: suspended(ConnectSdkTopicPage),
  ccsdkdotnetinitialization: suspended(ConnectSdkTopicPage),
  ccsdkdotnettransactions: suspended(ConnectSdkTopicPage),
  ccsdkdotneterrorhandling: suspended(ConnectSdkTopicPage),
  ccsdknodejs: suspended(ConnectSdkTopicPage),
  ccsdknodejsinstallation: suspended(ConnectSdkTopicPage),
  ccsdknodejsauthentication: suspended(ConnectSdkTopicPage),
  ccsdknodejsinitialization: suspended(ConnectSdkTopicPage),
  ccsdknodejstransactions: suspended(ConnectSdkTopicPage),
  ccsdknodejsrealtimemqtt: suspended(ConnectSdkTopicPage),
  ccsdknodejserrorhandling: suspended(ConnectSdkTopicPage),
  ccsdkphp: suspended(ConnectSdkTopicPage),
  ccsdkphpinstallation: suspended(ConnectSdkTopicPage),
  ccsdkphpauthentication: suspended(ConnectSdkTopicPage),
  ccsdkphpinitialization: suspended(ConnectSdkTopicPage),
  ccsdkphptransactions: suspended(ConnectSdkTopicPage),
  ccsdkphprealtimemqtt: suspended(ConnectSdkTopicPage),
  ccsdkphperrorhandling: suspended(ConnectSdkTopicPage),
  ccsdkplatformguides: suspended(SdkPlatformGuidesPage),
  ccsdkapireference: suspended(SdkApiReferencePage),
  cctransactions: suspended(CloudCreateTransactionPage),
  cctxcreate: suspended(CloudCreateTransactionPage),
  cctxresponse: suspended(CloudTransactionResponsePage),
  cctxcancel: suspended(CloudCancelTransactionPage),
  cctxrefund: suspended(CloudRefundTransactionPage),
  cchintstips: suspended(CloudHintsTipsPage),
  softposconnect: suspended(SoftposOverviewPage),
  spgettingstarted: suspended(SoftposGettingStartedPage),
  spappsetup: suspended(SoftposAppSetupPage),
  spsdk: suspended(SoftposConfigureSdkPage),
  spsdkconfigure: suspended(SoftposConfigureSdkPage),
  spsdkintegration: suspended(SoftposIntegrationPage),
  spsdkflow: suspended(SoftposIntegrationFlowPage),
  spsdkbestpractices: suspended(SoftposBestPracticesPage),
  sptransactions: suspended(SoftposCreateTransactionPage),
  sptxcreate: suspended(SoftposCreateTransactionPage),
  sptxresponse: suspended(SoftposTransactionResponsePage),
  sptxcancel: suspended(SoftposCancelTransactionPage),
  sptxrefund: suspended(SoftposRefundTransactionPage),
  sphintstips: suspended(SoftposHintsTipsPage),
  spcertifieddevices: suspended(SoftposCertifiedDevicesPage),
  vpayments: suspended(VPaymentsOverviewPage),
  vpaymentsgettingstarted: suspended(VPaymentsGettingStartedPage),
  vpaymentsapiintegration: suspended(VPaymentsApiIntegrationPage),
  vpaymentsvterminalapi: suspended(VPaymentsVTerminalApiPage),
  vpaymentspaylinksapi: suspended(VPaymentsPaylinksApiPage),
  vpaymentsactivation: suspended(VPaymentsActivationPage),
  vpaymentsiframe: suspended(VPaymentsIframeOverviewPage),
  vpaymentsiframehomepage: suspended(VPaymentsIframeHomepagePage),
  vpaymentsiframevterminal: suspended(VPaymentsIframeVterminalPage),
  vpaymentsiframepaylinks: suspended(VPaymentsIframePaylinksPage),
  vpaymentsiframerefund: suspended(VPaymentsIframeRefundPage),
  aiops: suspended(AiOpsPage),
  treasury: suspended(TreasuryPage),
  risk: suspended(RiskPage),
  capital: suspended(CapitalPage),
  tronc: suspended(TroncPage),
  identity: suspended(IdentityPage),
  settlement: suspended(SettlementPage),
  status: suspended(StatusPage),
  support: suspended(SupportPage),
  reporting: suspended(ReportingPage),
  sdks: suspended(SdksPage),
  phpsdk: suspended(PhpSdkPage),
  tssdk: suspended(TypeScriptSdkPage),
  pythonsdk: suspended(PythonSdkPage),
  gosdk: suspended(GoSdkPage),
  dotnetsdk: suspended(DotnetSdkPage),
  rubysdk: suspended(RubySdkPage),
  swiftsdk: suspended(SwiftSdkPage),
  kotlinsdk: suspended(KotlinSdkPage),
  settings: suspended(SettingsPage),
  customers: suspended(CustomersPage),
  companies: suspended(CompaniesPage),
  estate: suspended(EstatePage),
  products: suspended(ProductsPage),
  gettingstarted: suspended(GettingStartedPage),
  mcp: suspended(McpPage),
  mcpdocs: suspended(McpDocsPage),
  sandbox: suspended(SandboxPage),
  sandboxwebhooks: suspended(SandboxWebhooksPage),
  dbschema: suspended(DbSchemaPage),
  residuals: suspended(ResidualsPage),
  subscriptions: suspended(SubscriptionsPage),
  collections: suspended(CollectionsPage),
  paylinks: suspended(PayLinksPage),
  finance: suspended(FinancePage),
  aidocschat: suspended(AiDocsChatPage),
  aiintegration: suspended(AiIntegrationPage),
  productionreadiness: suspended(ProductionReadinessPage),
  compliance: suspended(CompliancePage),
  aioverview: suspended(AiOverviewPage),
  aiincident: suspended(AiIncidentPage),
  aiagents: suspended(AiAgentsPage),
  documentation: suspended(DocumentationPage),
  playground: suspended(PlaygroundPage),
  governance: suspended(GovernancePage),
  connectapikeys: suspended(ConnectApiKeysPage),
  connectestate: suspended(ConnectEstatePage),
  connectusers: suspended(ConnectUsersPage),
  connectcompanies: suspended(ConnectCompaniesPage),
  connectproducts: suspended(ConnectProductsPage),
  connectcustomers: suspended(ConnectCustomersPage),
};

function buildRoutes(): RouteObject[] {
  const featureRoutes: RouteObject[] = ROUTE_IDS.flatMap((id: RouteId) => {
    const navItem = NAV_BY_ID[id];
    // Defensive: on a full load every ROUTE_ID maps to a NavItem, but during HMR
    // router.tsx can re-run against a transiently stale NAV_BY_ID (before an
    // _navigation edit lands), leaving an id unmapped. Skip rather than throw.
    if (navItem === undefined) return [];
    const element = LAZY_ELEMENTS[id] ?? <PlaceholderPage routeId={id} />;
    return [{
      path: navItem.path ?? id,
      element: (
        <ProtectedRoute
          {...(navItem.permission !== undefined ? { permission: navItem.permission } : {})}
        >
          {element}
        </ProtectedRoute>
      ),
    }];
  });

  return [
    // Public — full-screen sign-in (outside the authenticated app shell).
    { path: 'login', element: <LoginPage /> },
    {
      element: <AppShell />,
      errorElement: <RouteErrorElement />,
      children: [
        { index: true, element: <Navigate to="/dashboard" replace /> },
        ...featureRoutes,
        { path: '*', element: <NotFound /> },
      ],
    },
  ];
}

export const routes: RouteObject[] = buildRoutes();

export const router = createBrowserRouter(routes);

/** Used by tests to render routes deterministically without a browser history. */
export function createTestRouter(initialEntries: string[] = ['/dashboard']): ReturnType<
  typeof createMemoryRouter
> {
  return createMemoryRouter(routes, { initialEntries });
}
